import { computed, reaction, observable } from 'mobx';
import { useEffect } from 'react';
import BaseStore from '@src/utils/store/base-store';
import useMount from '@src/utils/hooks/use-mount';
import PageStore from '@src/components/page/store';
import PageSwiperStore from '@src/components/swiper/page-swiper/store';
import { pageSwiperEffect } from '@src/components/swiper/page-swiper/config';
import ScrollTipStore from '@src/business-components/scroll-tip/store';
import NaviBarStore from '../../business-components/navibar/store';
import FirstPageStore from './components/first-page/store';
import ThirdPageStore from './components/third-page/store';
import FourthPageStore from './components/fourth-page/store';
import FifthPageStore from './components/fifth-page/store';
import SixthPageStore from './components/sixth-page/store';
import SeventhPageStore from './components/seventh-page/store';
import { useRootStore } from '@src/mobx-store/root-store';
import { getParameterByName } from '@src/utils/common-util.js';
import FooterStore from '@src/business-components/backtop-footer/components/footer/store';

class MainStore extends BaseStore {
  globalStore = {};
  // 全局store
  pageStore = new PageStore();
  // 首屏
  firstPageStore = new FirstPageStore({ mainStore: this });
  // 新闻
  newsStore = new ThirdPageStore({ mainStore: this });
  // 机甲
  mechaListStore = new FourthPageStore({ mainStore: this });
  // 特装机典藏版
  specialMechaStore = new FifthPageStore({ mainStore: this });
  // 游戏玩法
  featureListStore = new SixthPageStore({ mainStore: this });
  // 世界观
  worldViewStore = new SeventhPageStore({ mainStore: this });
  // 顶栏
  naviBarStore = new NaviBarStore();
  // 底部版权栏
  footerStore = new FooterStore({
    onBackTop: () => {
      this.pageSwiperStore.baseSwiperStore.onChange(0);
      this.isFooterActive = false;
    }
  });
  // 全屏swiper
  pageSwiperStore = new PageSwiperStore({
    effectMode: pageSwiperEffect.NORMAL,
    onPageSwiperChange: activeIndex => {
      this.globalStore?.toggleNavBarStickState(activeIndex > 0);
    }
  });
  scrollTipStore = new ScrollTipStore({
    mainStore: this,
    playKvVideo: () => {
      this.firstPageStore.kvVideoStore.replay();
    },
    pauseKvVideo: () => {
      this.firstPageStore.kvVideoStore.stop();
    },
    showFooter: () => {
      this.isFooterActive = true;
    }
  });

  baseDelayTime = 3000;
  allowSwitchPageSwiperTimer = null; // 允许切换页面定时器
  // 记得按页面轮播顺序来
  get pageStoreList() {
    return [this.firstPageStore, this.newsStore, this.mechaListStore, this.specialMechaStore, this.featureListStore, this.worldViewStore];
  }
  @observable pageSwiperIndex = 0;
  @observable showScrollTip = true;
  @observable
  pageLoading = {
    news: { state: false, delay: this.baseDelayTime, timer: null },
    mechaSwiper: { state: false, delay: this.baseDelayTime + 1000, timer: null },
    customMecha: { state: false, delay: this.baseDelayTime + 2000, timer: null },
    featureList: { state: false, delay: this.baseDelayTime + 3000, timer: null },
    wrod: { state: false, delay: 5000 }
  };
  @observable isRenderFifthPage = false;
  @observable isFooterActive = false;
  // 是否为最后一页
  @computed
  get isFinalPage() {
    return this.pageSwiperStore.baseSwiperStore.activeIndex >= this.pageSwiperStore.baseSwiperStore.size - 1;
  }
  // 是否为第一页
  @computed
  get isFirstPage() {
    return this.pageSwiperStore.baseSwiperStore.activeIndex === 0;
  }

  initLoadingTime = () => {
    for (let key in this.pageLoading) {
      const item = this.pageLoading[key];
      if (item.timer) clearTimeout(item.timer);
      item.timer = setTimeout(() => {
        item.state = true;
      }, item.delay);
    }
  };

  onBackTop = () => {
    this.pageSwiperStore.baseSwiperStore.onChange(0);
  };

  use() {
    super.use();
    // 全局store状态
    this.globalStore = useRootStore().globalStore;

    useMount(() => {
      const anchor = getParameterByName('anchor');
      if (anchor === 'mecha-unit') {
        this.pageSwiperStore.baseSwiperStore.onChange(3);
        const mechaId = getParameterByName('id');
        if (mechaId) {
          this.specialMechaStore.toSlide(mechaId);
        }
        if (IS_DEV_ENV) {
          this.$history.replace('/');
        } else {
          window.location.hash = '#/';
        }
      }
    });

    useMount(() => {
      this.initLoadingTime();
      reaction(
        () => this.firstPageStore.kvVideoStore.isPlaying,
        isPlaying => {
          this.scrollTipStore.playingKvVideo = isPlaying;
        }
      );
      reaction(
        () => this.isFirstPage,
        isFirstPage => {
          this.scrollTipStore.showPlayVideoBgBtn = isFirstPage;
        },
        {
          fireImmediately: true
        }
      );
      //#region 底部版权栏的控制逻辑
      reaction(
        () => this.isFooterActive,
        isActive => {
          clearTimeout(this.allowSwitchPageSwiperTimer);
          // 如果footer已经显示, 则禁止pageSwiper切换
          if (isActive) {
            this.pageSwiperStore.allowSwitch = false;
          } else if (this.isFinalPage) {
            this.allowSwitchPageSwiperTimer = setTimeout(() => {
              this.pageSwiperStore.allowSwitch = true;
            }, 300);
          } else {
            this.pageSwiperStore.allowSwitch = true;
          }
        }
      );
      const handleScroll = e => {
        /**
         * 1. 如果已经滑动到最后一屏，此时再次滑动鼠标，则可以控制footer是否显示
         * 2. 如果不在最后一屏，且此时footer已经显示，此时向上滑动鼠标，则可以控制footer隐藏
         */
        if (!this.pageSwiperStore.finalPageTransitionEnd && !this.isFooterActive) {
          return;
        }
        this.isFooterActive = e.deltaY > 0;
      };
      window.addEventListener('wheel', handleScroll);
      return () => {
        window.removeEventListener('wheel', handleScroll);

        this.globalStore?.toggleNavBarStickState(false);
      };
      //#endregion
    });

    // 滚动屏幕到对应位置的回调
    useEffect(
      () => {
        this.firstPageStore.isActive = false;
        this.newsStore.isActive = false;
        this.mechaListStore.isActive = false;
        this.specialMechaStore.isActive = false;
        this.featureListStore.isActive = false;
        this.worldViewStore.isActive = false;
        this.pageStoreList.forEach((pageStore, index) => {
          if (index === 3) {
            this.isRenderFifthPage = true;
          }

          if (this.pageSwiperStore.activeIndex === index) {
            pageStore?.playInAnimate?.();
          } else {
            pageStore?.leaveInAnimate?.();
          }
        });
      },
      [this.pageSwiperStore.activeIndex]
    );

    useEffect(
      () => {
        this.scrollTipStore.currentPageSize = this.pageSwiperStore.baseSwiperStore.size;
        if (
          this.pageSwiperStore.baseSwiperStore.activeIndex >=
          this.pageSwiperStore.baseSwiperStore.size /**当处于最后一屏时, 再滑动一屏展示footer展示,当前页码仍显示最后一页*/
        ) {
          this.scrollTipStore.currentPageNumber = this.pageSwiperStore.baseSwiperStore.size;
        } else {
          this.scrollTipStore.currentPageNumber = this.pageSwiperStore.baseSwiperStore.activeIndex + 1;
        }
      },
      [this.pageSwiperStore.baseSwiperStore.size, this.pageSwiperStore.baseSwiperStore.activeIndex]
    );
  }
}

export default MainStore;
